import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Locale, Translations } from './types';
import { en } from './locales/en';
import { zh } from './locales/zh';
import { ja } from './locales/ja';
import { ko } from './locales/ko';
import { fr } from './locales/fr';
import { de } from './locales/de';
import { es } from './locales/es';

const translations: Record<Locale, Translations> = {
    en,
    zh,
    ja,
    ko,
    fr,
    de,
    es,
};

interface I18nContextType {
    locale: Locale;
    setLocale: (locale: Locale) => void;
    t: Translations;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const LOCALE_KEY = 'getdev-locale';

export function I18nProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<Locale>(() => {
        // Try to get from localStorage
        const stored = localStorage.getItem(LOCALE_KEY) as Locale | null;
        const validLocales: Locale[] = ['en', 'zh', 'ja', 'ko', 'fr', 'de', 'es'];
        if (stored && validLocales.includes(stored)) {
            return stored;
        }

        // Detect browser language
        const browserLang = navigator.language.toLowerCase();
        if (browserLang.startsWith('zh')) return 'zh';
        if (browserLang.startsWith('ja')) return 'ja';
        if (browserLang.startsWith('ko')) return 'ko';
        if (browserLang.startsWith('fr')) return 'fr';
        if (browserLang.startsWith('de')) return 'de';
        if (browserLang.startsWith('es')) return 'es';
        return 'en';
    });

    const setLocale = (newLocale: Locale) => {
        setLocaleState(newLocale);
        localStorage.setItem(LOCALE_KEY, newLocale);
        document.documentElement.lang = newLocale;
    };

    useEffect(() => {
        document.documentElement.lang = locale;
    }, [locale]);

    const value: I18nContextType = {
        locale,
        setLocale,
        t: translations[locale],
    };

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    const context = useContext(I18nContext);
    if (!context) {
        throw new Error('useI18n must be used within I18nProvider');
    }
    return context;
}
